/**
 * <Toast> — Transient system feedback.
 *
 * Non-blocking notifications for:
 *   - Success confirmations ('Saved successfully')
 *   - Errors ('Connection failed')
 *   - Warnings ('Low disk space')
 *   - Info ('New update available')
 *
 * Built on @radix-ui/react-toast.
 *
 * Usage pattern — imperative via hook:
 *
 *   const { toast } = useToast();
 *
 *   toast({
 *     variant: 'success',
 *     title: 'Gespeichert',
 *     description: 'Dein Feld wurde aktualisiert.',
 *     duration: 5000,
 *   });
 *
 * Provider wiring in OverlayProvider (15.7.2).
 *
 * See: docs/architecture/OVERLAY_ARCHITECTURE.md §4.6
 */

import React, { createContext, useContext, useState, useCallback } from 'react';
import * as RadixToast from '@radix-ui/react-toast';

// =============================================================================
// Toast Context — imperative API
// =============================================================================

const ToastContext = createContext(null);

let toastIdCounter = 0;

/**
 * ToastProvider — mount at app root (inside OverlayProvider).
 * Holds queue of active toasts and exposes addToast to consumers via useToast.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {number} [props.defaultDuration=5000] - Default ms before auto-close
 * @param {'left'|'right'|'up'|'down'} [props.swipeDirection='right']
 */
export function ToastProvider({ children, defaultDuration = 5000, swipeDirection = 'right' }) {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((toast) => {
    const id = ++toastIdCounter;
    const duration = toast.duration !== undefined ? toast.duration : defaultDuration;
    setToasts((prev) => [...prev, { ...toast, id, duration }]);
    return id;
  }, [defaultDuration]);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ addToast, removeToast }}>
      <RadixToast.Provider swipeDirection={swipeDirection}>
        {children}
        {toasts.map((toast) => (
          <ToastItem key={toast.id} {...toast} onClose={() => removeToast(toast.id)} />
        ))}
        <RadixToast.Viewport
          className="fixed bottom-4 right-4 flex flex-col gap-2 w-[calc(100vw-32px)] sm:w-[380px] max-w-full m-0 list-none outline-none"
          style={{ zIndex: 'var(--aif-z-critical)' }}
        />
      </RadixToast.Provider>
    </ToastContext.Provider>
  );
}
ToastProvider.displayName = 'ToastProvider';

/**
 * useToast — hook for feature code to raise toasts.
 *
 * @returns {{ toast: (opts) => number, dismiss: (id) => void }}
 */
export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be called inside <ToastProvider>. Wrap your app with <OverlayProvider> which includes ToastProvider.');
  }
  return {
    toast: ctx.addToast,
    dismiss: ctx.removeToast,
  };
}

// =============================================================================
// Toast UI — single toast card
// =============================================================================

const VARIANT_CLASSES = {
  success: 'border-l-[var(--aif-color-success)]',
  error: 'border-l-[var(--aif-color-danger)]',
  warning: 'border-l-[var(--aif-color-warning)]',
  info: 'border-l-[var(--aif-color-info)]',
  default: 'border-l-[var(--aif-border-default)]',
};

const VARIANT_ICONS = {
  success: '✓',
  error: '✕',
  warning: '⚠',
  info: 'ℹ',
};

const VARIANT_ICON_COLORS = {
  success: 'bg-green-500',
  error: 'bg-red-500',
  warning: 'bg-amber-500',
  info: 'bg-blue-500',
};

const VARIANT_ARIA_ROLE = {
  error: 'alert',
  warning: 'alert',
  success: 'status',
  info: 'status',
  default: 'status',
};

/**
 * ToastItem — single toast card inside the Viewport.
 */
function ToastItem({
  id,
  variant = 'default',
  title,
  description,
  action,
  duration,
  onClose,
}) {
  const variantClass = VARIANT_CLASSES[variant] || VARIANT_CLASSES.default;
  const icon = VARIANT_ICONS[variant];
  const iconColor = VARIANT_ICON_COLORS[variant];
  const role = VARIANT_ARIA_ROLE[variant] || 'status';

  return (
    <RadixToast.Root
      duration={duration}
      onOpenChange={(open) => { if (!open) onClose(); }}
      role={role}
      className={`relative flex items-start gap-3 p-4 rounded-[var(--aif-radius-md)] border border-[var(--aif-border-default)] border-l-4 ${variantClass} bg-[var(--aif-bg-elevated)] shadow-[var(--aif-shadow-lg)] text-[var(--aif-text-primary)] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-right-full data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=cancel]:translate-x-0 data-[swipe=end]:animate-out data-[swipe=end]:slide-out-to-right-full`}
    >
      {icon && iconColor && (
        <div
          className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-white text-sm font-bold ${iconColor}`}
          aria-hidden="true"
        >
          {icon}
        </div>
      )}
      <div className="flex-1 min-w-0">
        {title && (
          <RadixToast.Title className="font-semibold text-sm text-[var(--aif-text-primary)]">
            {title}
          </RadixToast.Title>
        )}
        {description && (
          <RadixToast.Description className="text-sm text-[var(--aif-text-secondary)] mt-0.5">
            {description}
          </RadixToast.Description>
        )}
        {action && (
          <div className="mt-2">
            <RadixToast.Action
              altText={action.altText || action.label}
              asChild
            >
              <button
                onClick={action.onClick}
                className="text-sm font-medium text-[var(--aif-color-primary)] hover:underline"
              >
                {action.label}
              </button>
            </RadixToast.Action>
          </div>
        )}
      </div>
      <RadixToast.Close
        className="flex-shrink-0 ml-2 text-[var(--aif-text-muted)] hover:text-[var(--aif-text-primary)] transition-colors"
        aria-label="Schließen"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M4 4L12 12M12 4L4 12" />
        </svg>
      </RadixToast.Close>
    </RadixToast.Root>
  );
}
ToastItem.displayName = 'ToastItem';
